import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  AppBar,
  Toolbar,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Box,
  Container,
  Dialog,
  DialogTitle,
  DialogContent,
} from "@mui/material";
import ReactPlayer from "react-player";
import CloseIcon from "@mui/icons-material/Close";
import MenuIcon from "@mui/icons-material/Menu";
import AccountCircle from "@mui/icons-material/AccountCircle";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation, Outlet } from "react-router-dom";

// Components
import DashboardGuidedTour from "../dashboard/DashboardGuidedTour";
import NotificationToast from "../components/scheduler/NotificationToast";
import UserAccountPopover from "../components/layout/UserAccountPopover";

// Icons
import VideoCameraIcon from "@mui/icons-material/Videocam";
import QuestionMarkCircle from "@mui/icons-material/Help";

// Utils and services
import { useAuthStore } from "../stores/zustand";
import { useTourStore } from "../stores/zustand";
import { authApi, settingsApi } from "../services";
import { useHomeDataQuery } from "../stores/tanstack/queries/useHomeDataQuery";
import { useFirebaseNotifications } from "../hooks/useFirebaseNotifications";
import { useQueryClient } from "@tanstack/react-query";
import { useLocalNotifications } from "../hooks/useLocalNotifications";

/**
 * MainContainerMobile Component
 *
 * Mobile-optimized main application shell and layout wrapper. Responsible for:
 * - Mobile AppBar with menu, help, video, and account controls
 * - Drawer-based navigation for mobile (replaces sidebar)
 * - Routing outlet for nested page components (calendar, library, settings, feedback)
 * - Firebase toast notifications display
 * - Guided tour and video tutorial dialogs
 * - StartSite overlay for course users (first-time onboarding)
 * - Language switching with backend sync and Electron integration
 * - Proper authentication lifecycle management with modern state patterns
 *
 * Uses HTTP cookie-based authentication (no JWT tokens).
 * Manages TanStack Query cache invalidation on language changes.
 * Syncs Electron menu language changes with frontend i18n.
 *
 * @returns {JSX.Element} Mobile-optimized application container
 */
const MainContainerMobile = () => {
  // Translation and routing hooks
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  // Auth and product context
  const {
    user,
    currentProduct,
    preferredLanguage,
    clearAuth,
    setPreferredLanguage,
  } = useAuthStore();
  const { data: homeData } = useHomeDataQuery();
  const { nextStep } = useTourStore();
  const queryClient = useQueryClient();

  // Firebase notifications hook - ADD THIS
  const {
    showToast,
    currentNotification,
    dismissToast,
    getNotificationPermission,
    handleEnablePushNotifications,
    isElectron,
  } = useFirebaseNotifications();

  useLocalNotifications({
    enabled: true,
    autoStartSync: true,
    homeData: homeData,
  });

  // Memoize feature access flags to prevent unnecessary recalculations
  const { hasCourseAccess, hasCorporateAccess } = useMemo(
    () => ({
      hasCourseAccess: currentProduct === "preventive_course",
      hasCorporateAccess: currentProduct === "corporate",
    }),
    [currentProduct],
  );

  // Derive current page from URL pathname
  // Used for sidebar highlighting and conditional content rendering
  const currentContent = useMemo(() => {
    if (location.pathname.includes("/library")) return "library";
    if (location.pathname.includes("/settings")) return "settings";
    if (location.pathname.includes("/feedback")) return "feedback";
    if (location.pathname.includes("/schedule")) return "schedule";
    if (location.pathname.includes("/calendar")) return "schedule";
    return "schedule"; // default
  }, [location.pathname]);

  // Extract authentication flow data from route location state
  const isFirstLogin = location.state?.isFirstLogin;

  // UI state management for dialogs and tours
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [isTourOpen, setIsTourOpen] = useState(false);
  const [isVideoDialogOpen, setIsVideoDialogOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [libraryId, setLibraryId] = useState(null);

  // Drawer and menu handlers
  const toggleDrawer = useCallback(() => {
    setDrawerOpen(!drawerOpen);
  }, [drawerOpen]);

  const handleAccountClick = useCallback((event) => {
    setAnchorEl(event.currentTarget);
  }, []);

  const handleAccountClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  // Update user password with validation of current password
  const handlePasswordUpdate = useCallback(
    async (currentPassword, newPassword) => {
      try {
        await settingsApi.updatePassword(currentPassword, newPassword);
        return { success: true, message: t("passwordUpdated") };
      } catch (error) {
        console.error("Failed to update password:", error);

        if (error.response?.status === 403) {
          return { success: false, message: t("incorrectCurrentPassword") };
        }

        return { success: false, message: t("passwordUpdateFailed") };
      }
    },
    [t],
  );
  // Toggle between English and German language
  // Syncs with AuthStore, i18n, backend, and Electron if available
  const handleLanguageToggle = useCallback(async () => {
    const newLanguage = preferredLanguage === "en" ? "de" : "en";

    //  Update Zustand
    setPreferredLanguage(newLanguage);

    //  Sync i18n
    i18n.changeLanguage(newLanguage);

    // Invalidate all language-dependent queries
    queryClient.invalidateQueries(["homeData"]);
    queryClient.invalidateQueries(["preferences"]);
    queryClient.invalidateQueries(["videos"]);
    queryClient.invalidateQueries(["settings"]);
    queryClient.invalidateQueries(["therapistHistory"]);

    //  Update backend
    try {
      await settingsApi.updateLanguage(newLanguage);
    } catch (error) {
      console.error(
        "[MainContainerMobile] Failed to update language in backend:",
        error,
      );
    }

    //  Notify Electron if present
    if (window.electronAPI?.language) {
      window.electronAPI.language.notifyChange(newLanguage);
    }
  }, [preferredLanguage, setPreferredLanguage, i18n, queryClient]);

  // Listen for language changes from Electron menu (desktop app integration)
  // When user changes language in Electron app, sync it with React i18n
  useEffect(() => {
    if (window.electronAPI?.language) {
      const cleanup = window.electronAPI.language.onChange((newLanguage) => {
        i18n.changeLanguage(newLanguage);

        settingsApi.updateLanguage(newLanguage).catch((error) => {
          console.error("Failed to update language in backend:", error);
        });

        // Invalidate language-dependent queries
        queryClient.invalidateQueries(["homeData"]);
        queryClient.invalidateQueries(["preferences"]);
        queryClient.invalidateQueries(["videos"]);
        queryClient.invalidateQueries(["settings"]);
        queryClient.invalidateQueries(["therapistHistory"]);
      });

      return cleanup;
    }
  }, [i18n, queryClient]);

  // Logout: clear caches, auth state, and navigate to login
  // Properly cleans up TanStack Query cache and Zustand auth store
  const handleLogout = useCallback(async () => {
    try {
      await authApi.logout();
    } catch (error) {
      console.error("Logout API call failed:", error);
    }

    // Clear TanStack Query cache
    queryClient.clear();

    // Clear AuthStore
    clearAuth();

    if (window.electronAPI?.navigation) {
      try {
        await window.electronAPI.navigation.navigateToAuthenticatedState(false);
      } catch (navError) {
        console.warn(
          "[MainContainerMobile] Electron navigation failed:",
          navError,
        );
        navigate("/");
      }
    } else {
      navigate("/");
    }
  }, [queryClient, clearAuth, navigate]);

  // Video dialog handlers for training video playback
  const handleOpenVideoDialog = useCallback(() => {
    setIsVideoDialogOpen(true);
  }, []);

  const handleCloseVideoDialog = useCallback(() => {
    setIsVideoDialogOpen(false);
  }, []);

  // Get correct video URL based on current language
  // English and German versions use different Vimeo videos
  const videoUrl = useMemo(() => {
    return preferredLanguage === "en"
      ? "https://vimeo.com/1031253750"
      : "https://vimeo.com/1033152791";
  }, [preferredLanguage]);

  // Tour handlers
  const handleStartTour = useCallback(() => {
    setIsTourOpen(true);
  }, []);

  const handleCloseTour = useCallback(() => {
    setIsTourOpen(false);
  }, []);

  // Navigate to notification test page
  const handleTestNotifications = useCallback(() => {
    navigate("/home/test");
  }, [navigate]);

  // Handle navigation between content sections
  const handleContentChange = useCallback(
    (contentName) => {
      setDrawerOpen(false);

      // Trigger tour next step if tour is active
      nextStep();

      // Navigate based on content type
      if (contentName === "library") {
        // Force VideoLibraryComponent re-mount by updating libraryKey
        navigate(`/home/library${libraryId ? `?videoId=${libraryId}` : ""}`);
      } else if (contentName === "settings") {
        navigate(`/home/settings`);
      } else if (contentName === "feedback") {
        navigate(`/home/feedback`);
      } else if (contentName === "schedule") {
        navigate(`/home/calendar`);
      }
    },
    [navigate, libraryId, nextStep],
  );

  // Update library ID from URL when navigating
  useEffect(() => {
    if (location.pathname.includes("/library")) {
      const searchParams = new URLSearchParams(location.search);
      const idParam = searchParams.get("videoId");
      if (idParam) {
        setLibraryId(idParam);
      }
    }
  }, [location.search, location.pathname]);

  // Menu items for drawer navigation
  const menuItems = useMemo(
    () => [
      { text: t("schedule"), value: "schedule" }, // calendar renamed to schedule
      { text: t("feedback"), value: "feedback" },
      { text: t("library"), value: "library" },
      { text: t("settings"), value: "settings" },
    ],
    [t],
  );

  const open = Boolean(anchorEl);
  const id = open ? "account-popover" : undefined;

  return (
    <Box>
      {/* Firebase notification toast display */}
      {showToast && currentNotification && (
        <NotificationToast
          notification={currentNotification}
          onDismiss={dismissToast}
        />
      )}

      {/* Mobile App Bar */}
      <AppBar position="fixed" sx={{ backgroundColor: "#00003e" }}>
        <Toolbar
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <IconButton
            edge="start"
            color="inherit"
            aria-label="menu"
            onClick={toggleDrawer}
          >
            <MenuIcon />
          </IconButton>

          {/* App Logo and Product Badge */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
            }}
          >
            {/* Product Type Badge */}
            {hasCorporateAccess && (
              <Box
                sx={{
                  backgroundColor: "rgba(255, 255, 255, 0.2)",
                  color: "white",
                  px: 1.5,
                  py: 0.25,
                  borderRadius: 1,
                  fontSize: "0.75rem",
                  fontWeight: "medium",
                }}
              >
                Corporate
              </Box>
            )}
            {hasCourseAccess && (
              <Box
                sx={{
                  backgroundColor: "rgba(255, 255, 255, 0.2)",
                  color: "white",
                  px: 1.5,
                  py: 0.25,
                  borderRadius: 1,
                  fontSize: "0.75rem",
                  fontWeight: "medium",
                }}
              >
                Preventive Course
              </Box>
            )}
            {/* Training Video Button */}
            <IconButton
              className="video-button"
              size="large"
              aria-label="training video"
              color="inherit"
              onClick={handleOpenVideoDialog}
            >
              <VideoCameraIcon style={{ fontSize: "32px" }} />
            </IconButton>
            {/* Guided Tour Button */}
            <IconButton
              className="tour-button"
              size="large"
              aria-label="guided tour"
              color="inherit"
              onClick={handleStartTour}
            >
              <QuestionMarkCircle style={{ fontSize: "32px" }} />
            </IconButton>
            {/* Account Menu Button */}
            <IconButton
              size="large"
              aria-label="account menu"
              aria-controls={id}
              aria-haspopup="true"
              color="inherit"
              onClick={handleAccountClick}
            >
              <AccountCircle style={{ fontSize: "32px" }} />
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Mobile Navigation Drawer */}
      <Drawer
        anchor="left"
        open={drawerOpen}
        onClose={toggleDrawer}
        PaperProps={{
          sx: {
            backgroundColor: "#00003e",
            color: "white",
          },
        }}
      >
        <Box sx={{ width: "220px" }}>
          <List sx={{ paddingTop: 12 }}>
            {menuItems.map((item, index) => (
              <ListItem
                button
                key={index}
                onClick={() => handleContentChange(item.value)}
              >
                <ListItemText primary={item.text} />
              </ListItem>
            ))}
          </List>
        </Box>
      </Drawer>

      <UserAccountPopover
        open={open}
        anchorEl={anchorEl}
        onClose={handleAccountClose}
        onLanguageToggle={handleLanguageToggle}
        onPasswordUpdate={handlePasswordUpdate}
        onLogout={handleLogout}
        currentLanguage={preferredLanguage}
        userEmail={user?.email || ""}
        notificationPermission={getNotificationPermission()}
        onEnableNotifications={handleEnablePushNotifications}
        isElectron={isElectron}
        onTestNotifications={handleTestNotifications}
      />

      {/* Main Content Area */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          paddingTop: 0,
          paddingLeft: 0,
        }}
      >
        <Toolbar />
        <Container>
          <Outlet context={{ isFirstLogin, isMobile: true }} />{" "}
        </Container>
      </Box>

      {/* Guided Tour Modal */}
      <DashboardGuidedTour
        isTourOpen={isTourOpen}
        closeTour={handleCloseTour}
        currentPage={currentContent}
      />

      {/* Training Video Dialog */}
      <Dialog
        open={isVideoDialogOpen}
        onClose={handleCloseVideoDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <IconButton
            onClick={handleCloseVideoDialog}
            style={{ position: "absolute", right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <ReactPlayer
            url={videoUrl}
            controls={true}
            width="100%"
            height="300px"
            onEnded={handleCloseVideoDialog}
          />
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default MainContainerMobile;
